import React, { useState } from 'react';
import {
  Badge, Button, Col, Form, Modal,
} from 'react-bootstrap';
import Select, { GroupBase, StylesConfig } from 'react-select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import backgroundIcon from '../../../../../assets/images/ui/background-icon.svg';
import { BookLocation } from '../../../../../dorian-shared/types/bookLocation/bookLocation';
import { customSelectStyles } from '../LocationSelect/customSelectStyles';
import { buildStepBackgroundOptions, StepBackgroundOption } from './stepDataFields';

// The shared styles are declared against LocationSelect's own option type but
// only ever read react-select's generic state (isFocused/isSelected), never an
// option's fields — so they are reused here rather than duplicated.
const selectStyles = customSelectStyles as unknown as StylesConfig<
  StepBackgroundOption,
  false,
  GroupBase<StepBackgroundOption>
>;

// The select renders inside the modal without a portal: the app's modal sits at
// z-index 99999, so a portalled menu would fall behind it, and the modal content
// does not clip overflow, so the option list can extend past the modal's edge.

function NoOptionsMessage() {
  return (
    <div style={{ color: '#999', padding: '10px', textAlign: 'center' }}>
      No locations available
    </div>
  );
}

interface StepsFormBackgroundProps {
  disabled?: boolean;
  /** The location alias currently overriding this step's background, or ''. */
  value: string;
  onChange: (alias: string) => void;
  locations: BookLocation[];
  displayControl?: boolean;
  /**
   * False for a Video step: a video-backed location behind a video step is the
   * conjunction the node-level guard already refuses. See below.
   */
  allowVideoLocations?: boolean;
  stepIndex?: number;
}

/**
 * Per-step background override.
 *
 * Rendered as a small icon button beside the audio button, opening the picker
 * in a modal, so the step's text box keeps its width on every line (creators'
 * feedback, 2026-09-17). The button carries a badge when the step has its own
 * background, and its tooltip names the chosen location.
 *
 * The selected value is a Location ALIAS, not an id — that is what the runtime
 * engine expects on `step.data.background`, and it is the same string the
 * branch-level `scene` carries. The client routes the alias through its normal
 * scene machinery, so a video-backed location plays its video exactly as it
 * would at node level.
 *
 * Video-backed locations are therefore offered for every step type EXCEPT a
 * Video step, where the combination reproduces the failure the node-level guard
 * in `AddBranch.formValidated` exists to prevent: the scene's ambient video
 * player is destroyed mid-playback when the step's own video loads, and nothing
 * restores it, so the reader is left on the step clip's frozen last frame for
 * the remainder of the node.
 *
 * PHASE B: relaxing this belongs to the same gated change that relaxes the
 * node-level guard, and must not be done independently. `findVideoBackgroundConflicts`
 * in stepDataFields.ts is the save-time half of the same rule.
 *
 * The value is stored as a `background` row in the step's `data` table; the
 * always-mounted StepsFormData inputs are what serialize it on save, so this
 * control deliberately renders no form input of its own. Choosing a location
 * applies to the form state at once; the node's own Save writes it.
 */
export function StepsFormBackground({
  disabled,
  value,
  onChange,
  locations,
  displayControl,
  allowVideoLocations = true,
  stepIndex,
}: StepsFormBackgroundProps) {
  const [show, setShow] = useState(false);

  if (displayControl === false) {
    return null;
  }

  const selectable = (locations ?? []).filter((location) => !!location.alias);
  const groupedOptions = buildStepBackgroundOptions(locations, allowVideoLocations);
  const allOptions = groupedOptions.flatMap((group) => group.options);

  // A step can reference an alias that is not currently offered: either the
  // location was renamed or deleted, or it is a video-backed location on a step
  // that has since been retyped to Video. Keep showing the stored value rather
  // than silently resetting it, but say which of the two it is. The Video case is
  // refused at save time by findVideoBackgroundConflicts.
  const knownOption = allOptions.find((option) => option.value === value);
  const unofferedLocation = knownOption
    ? undefined
    : selectable.find((location) => location.alias === value);
  const selectedOption: StepBackgroundOption | null = knownOption ?? (value
    ? {
      label: unofferedLocation
        ? `${unofferedLocation.title} (not allowed on a Video step)`
        : `${value} (missing)`,
      value,
      type: 'image' as const,
      alias: value,
    }
    : allOptions[0]) ?? null;

  const handleChange = (option: StepBackgroundOption | null) => {
    onChange(option ? option.value : '');
  };

  const handleFilterOption = (
    option: FilterOptionOption<StepBackgroundOption>,
    inputValue: string,
  ) => {
    const needle = inputValue.toLowerCase();
    const isLabelMatch = option.label.toLowerCase().includes(needle);
    const isAliasMatch = !!option.data?.alias?.toLowerCase().includes(needle);
    const isGroupMatch = !!option.data?.type?.toLowerCase().includes(needle);
    return isLabelMatch || isAliasMatch || isGroupMatch;
  };

  const formatOptionLabel = (option: StepBackgroundOption) => (
    <span title={option.alias ? `${option.label} (${option.alias})` : option.label}>
      {option.label}
    </span>
  );

  const inputId = `stepBackground-${stepIndex ?? 0}`;
  const label = `Step ${stepIndex ?? 0} background`;
  const hasOverride = !!value;
  const buttonTitle = hasOverride && selectedOption
    ? `Background: ${selectedOption.label}`
    : 'Background: node location';

  return (
    <Col sm="auto" className="px-1 position-relative">
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={() => setShow(true)}
        title={buttonTitle}
        aria-label={label}
      >
        <img
          src={backgroundIcon}
          alt=""
          style={{
            width: '18px',
            height: '16px',
          }}
        />
      </Button>
      {hasOverride && (
        <span className="boxBadge" style={{ top: '-8px', right: '-8px' }}>
          <Badge
            pill
            variant="indicator"
          >
            1
          </Badge>
        </span>
      )}
      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Step background</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label htmlFor={inputId}>
              {label}
            </Form.Label>
            <Select
              inputId={inputId}
              aria-label={label}
              isDisabled={disabled}
              className="react-select-container"
              classNamePrefix="react-select"
              options={groupedOptions}
              value={selectedOption}
              onChange={handleChange}
              placeholder="Background"
              components={{ NoOptionsMessage }}
              styles={selectStyles}
              filterOption={handleFilterOption}
              formatOptionLabel={formatOptionLabel}
            />
          </Form.Group>
          <Form.Text className="text-muted">
            Node location is the default. Pick a location to show a different background from this line on.
            The choice is saved with the node.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShow(false)}
          >
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </Col>
  );
}
